import React, { useEffect, useRef, useState, useCallback } from 'react';

const Pager = ({ pages = [] }) => {
	const containerRef = useRef(null);
	const [index, setIndex] = useState(0);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		let ticking = false;
		const onScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					const w = el.clientWidth || 1;
					const i = Math.round(el.scrollLeft / w);
					if (i !== index) setIndex(i);
					ticking = false;
				});
				ticking = true;
			}
		};
		el.addEventListener('scroll', onScroll, { passive: true });
		return () => el.removeEventListener('scroll', onScroll);
	}, [index]);

	const goTo = useCallback((i) => {
		const el = containerRef.current;
		if (!el) return;
		const w = el.clientWidth || 1;
		el.scrollTo({ left: i * w, behavior: 'smooth' });
		setIndex(i);
	}, []);

	const canPrev = index > 0;
	const canNext = index < pages.length - 1;

	const prev = useCallback(() => {
		if (canPrev) goTo(index - 1);
	}, [canPrev, index, goTo]);

	const next = useCallback(() => {
		if (canNext) goTo(index + 1);
	}, [canNext, index, goTo]);

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'ArrowLeft') prev();
			if (e.key === 'ArrowRight') next();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [prev, next]);

	if (!pages.length) return null;

	return (
		<div className="pager-root">
			<div className="pager-container" ref={containerRef}>
				{pages.map((Page, i) => (
					<section className="pager-slide" key={i}>
						{Page}
					</section>
				))}
			</div>
			<button
				className={`pager-arrow left ${!canPrev ? 'disabled' : ''}`}
				onClick={prev}
				aria-label="Previous page"
				disabled={!canPrev}
			>
				<i className="pi pi-chevron-left" />
			</button>
			<button
				className={`pager-arrow right ${!canNext ? 'disabled' : ''}`}
				onClick={next}
				aria-label="Next page"
				disabled={!canNext}
			>
				<i className="pi pi-chevron-right" />
			</button>
			<div className="pager-dots" role="tablist" aria-label="Pages">
				{pages.map((_, i) => (
					<button
						key={i}
						className={`pager-dot ${i === index ? 'active' : ''}`}
						onClick={() => goTo(i)}
						aria-label={`Go to page ${i + 1}`}
						aria-selected={i === index}
						role="tab"
					/>
				))}
			</div>
		</div>
	);
};

export default Pager;

